import { useEffect, useMemo, useState } from 'react';
import { Person } from '../components/Avatar';
import { useNavigate, useParams } from 'react-router-dom';
import { api, errorMessage } from '../lib/api';
import type { PublicUser } from '../lib/types';
import type { TestSummary } from '../lib/tests';
import { PageHeader } from '../components/PageHeader';
import { groupIntoTests } from '../lib/library';
import { Banner } from '../components/Banner';
import { useConfirm } from '../components/Confirm';
import { SessionResults } from '../components/SessionResults';
import {
  clockTime,
  dayLabel,
  SLOT_NAMES,
  timeLeft,
  type SessionReport,
  type SessionView,
} from '../lib/sessions';

interface Attendance {
  seated: number;
  started: number;
  finished: number;
}

/**
 * One sitting of a test day.
 *
 * Everything about a session is here rather than spread over a wizard: the
 * papers on the desks, who is in the room, whether the papers are dealt, and —
 * once it has run — what it produced. A teacher building a session over a week
 * comes back to the same screen each time and sees what is still missing.
 */
export function SessionPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { ask, dialog } = useConfirm();

  const [session, setSession] = useState<SessionView | null>(null);
  const [attendance, setAttendance] = useState<Attendance | null>(null);
  const [report, setReport] = useState<SessionReport | null>(null);
  const [library, setLibrary] = useState<TestSummary[]>([]);
  const [students, setStudents] = useState<PublicUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [paperQuery, setPaperQuery] = useState('');
  const [studentQuery, setStudentQuery] = useState('');

  async function load() {
    try {
      const [one, papers, register] = await Promise.all([
        api.get<{ session: SessionView; attendance: Attendance }>(`/sessions/${id}`),
        api.get<{ tests: TestSummary[] }>('/tests'),
        api.get<{ members: PublicUser[] }>('/users', { params: { role: 'STUDENT' } }),
      ]);
      setSession(one.data.session);
      setAttendance(one.data.attendance);
      setLibrary(papers.data.tests);
      setStudents(register.data.members);
      setError(null);
    } catch (err) {
      setError(errorMessage(err, 'Could not load that session'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  /** The results are worth fetching only once somebody has sat something. */
  useEffect(() => {
    if (!attendance || attendance.started === 0) return;
    api
      .get<SessionReport>(`/sessions/${id}/results`)
      .then(({ data }) => setReport(data))
      .catch(() => setReport(null));
  }, [id, attendance]);

  const chosen = useMemo(
    () => new Set(session?.papers.map((paper) => paper.id) ?? []),
    [session],
  );
  const seated = useMemo(
    () => new Set(session?.candidates.map((candidate) => candidate.id) ?? []),
    [session],
  );

  /**
   * The library as whole tests, with the loose papers under them.
   *
   * A centre chooses Test 1 for a session, not its Listening and then its
   * Reading and then its Writing. Ticking a test puts all of its papers on the
   * desks at once; a paper belonging to no test is still there underneath, for
   * a session that is only one module.
   */
  const { wholeTests, loosePapers } = useMemo(() => {
    const term = paperQuery.trim().toLowerCase();
    const matches = (paper: TestSummary) =>
      !term ||
      paper.title.toLowerCase().includes(term) ||
      (paper.setName ?? '').toLowerCase().includes(term);

    const published = library.filter((paper) => paper.status === 'PUBLISHED').filter(matches);
    // Grouped the same way the library groups them, which is what keeps a
    // centre's own "Test 1" apart from a "Test 1" lent by another centre —
    // ticking one of the two must not put six papers on the desks.
    const { tests, loose } = groupIntoTests(published);

    return {
      wholeTests: tests.map((test) => ({
        name: test.name,
        sharedFrom: test.owned ? null : test.platform ? 'the platform' : test.sharedFrom,
        // One paper per module: a spare of a module is not part of the test a
        // candidate sits.
        papers: test.slots
          .map((slot) => slot.paper)
          .filter((paper): paper is TestSummary => Boolean(paper)),
      })),
      loosePapers: loose.sort((a, b) => a.title.localeCompare(b.title)),
    };
  }, [library, paperQuery]);

  const roster = useMemo(() => {
    const term = studentQuery.trim().toLowerCase();
    return students.filter(
      (student) =>
        !term ||
        student.fullName.toLowerCase().includes(term) ||
        (student.candidateNumber ?? '').includes(term) ||
        (student.groupName ?? '').toLowerCase().includes(term),
    );
  }, [students, studentQuery]);

  /** The classes on the register, so a whole class can be seated at once. */
  const classes = useMemo(() => {
    const names = new Set<string>();
    for (const student of students) if (student.groupName) names.add(student.groupName);
    return [...names].sort();
  }, [students]);

  const open = session !== null && session.state !== 'CLOSED';

  async function save<T>(work: () => Promise<T>, said?: string) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await work();
      if (said) setNotice(said);
      await load();
    } catch (err) {
      setError(errorMessage(err, 'That did not save'));
    } finally {
      setBusy(false);
    }
  }

  /** A whole test on the desks, or off them, in one tick. */
  const toggleTest = (ids: string[], remove: boolean) =>
    save(() =>
      api.put(`/sessions/${id}/papers`, {
        testIds: remove
          ? [...chosen].filter((each) => !ids.includes(each))
          : [...new Set([...chosen, ...ids])],
      }),
    );

  const togglePaper = (testId: string) =>
    save(() =>
      api.put(`/sessions/${id}/papers`, {
        testIds: chosen.has(testId)
          ? [...chosen].filter((each) => each !== testId)
          : [...chosen, testId],
      }),
    );

  const toggleStudent = (studentId: string) =>
    save(() =>
      api.put(`/sessions/${id}/candidates`, {
        studentIds: seated.has(studentId)
          ? [...seated].filter((each) => each !== studentId)
          : [...seated, studentId],
      }),
    );

  const seatClass = (name: string) => {
    const wanted = students.filter((s) => s.groupName === name).map((s) => s.id);
    const missing = wanted.filter((studentId) => !seated.has(studentId));
    return save(
      () =>
        api.put(`/sessions/${id}/candidates`, {
          studentIds: missing.length > 0 ? [...seated, ...missing] : [...seated].filter((studentId) => !wanted.includes(studentId)),
        }),
      missing.length > 0 ? `${name} is sitting this session.` : `${name} taken out of this session.`,
    );
  };

  async function setPapersOnRoom() {
    setBusy(true);
    setError(null);
    setNotice(null);
    setWarning(null);
    try {
      const { data } = await api.post<{
        created: number;
        adopted: number;
        skipped: string[];
        alreadySat: string[];
      }>(`/sessions/${id}/set`, {});
      const parts = [
        data.created > 0
          ? `${data.created} paper${data.created === 1 ? '' : 's'} set.`
          : 'Nothing new to set.',
        // A paper somebody already held has been folded into the session, so it
        // now runs on the room's clock and counts in the room's results.
        data.adopted > 0
          ? `${data.adopted} paper${data.adopted === 1 ? '' : 's'} they already held moved into this session.`
          : '',
        data.skipped.length > 0
          ? `Not published, so left out: ${data.skipped.join(', ')}.`
          : '',
      ].filter(Boolean);
      setNotice(parts.join(' '));
      // The one thing here a teacher has to act on: somebody cannot sit a
      // paper twice, so they need a different one or they sit out.
      setWarning(
        data.alreadySat.length > 0
          ? `${data.alreadySat.join(', ')} ${data.alreadySat.length === 1 ? 'has' : 'have'} already sat one of these papers and cannot sit it again. Give them a different test, or take them out of the session.`
          : null,
      );
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Those papers could not be set'));
    } finally {
      setBusy(false);
    }
  }

  async function closeNow() {
    const yes = await ask({
      title: 'Close this session now?',
      body: (
        <>
          <p>Every paper still open in it stops where it is and is marked as it stands.</p>
          <p>A closed session cannot be reopened.</p>
        </>
      ),
      confirmLabel: 'Close the session',
      tone: 'danger',
    });
    if (!yes) return;
    await save(() => api.post(`/sessions/${id}/close`, {}), 'The session is closed.');
  }

  async function remove() {
    /*
     * Under way, it is called off rather than deleted, and the dialog says
     * what that does to the people in the room: closing would mark every open
     * paper, which is exactly what a session set by mistake must not do.
     */
    const running = session?.state === 'RUNNING';
    const yes = await ask({
      title: running ? 'Cancel this session now?' : 'Take this session out of the diary?',
      body: running ? (
        <>
          <p>
            Every paper still open in it stops where it is and is <strong>not marked</strong>.
            Candidates who have not started can no longer start.
          </p>
          <p>
            Anyone who has already handed a paper in keeps that result — clear it in Results if it
            should not count.
          </p>
          <p>The session comes off the timetable. This cannot be undone.</p>
        </>
      ) : (
        <p>The papers it set are withdrawn. Nobody has sat it, so nothing is lost.</p>
      ),
      confirmLabel: running ? 'Cancel the session' : 'Delete the session',
      cancelLabel: running ? 'Keep it running' : undefined,
      tone: 'danger',
    });
    if (!yes) return;
    try {
      await api.delete(`/sessions/${id}`);
      navigate('/schedule');
    } catch (err) {
      setError(errorMessage(err, 'That session could not be deleted'));
    }
  }

  if (loading) return <p className="muted">Loading…</p>;
  if (!session) return <Banner tone="error">{error ?? 'No session by that id.'}</Banner>;

  return (
    <>
      {dialog}
      <PageHeader
        eyebrow={`${dayLabel(session.day)} · ${SLOT_NAMES[session.slot]}`}
        title={session.name || `${SLOT_NAMES[session.slot]} session`}
        meta={
          <p className="muted">
            {clockTime(session.startsAt)}–{clockTime(session.endsAt)} ·{' '}
            {session.state === 'RUNNING' ? (
              <strong>{timeLeft(session.endsAt)}</strong>
            ) : session.state === 'CLOSED' ? (
              'closed'
            ) : (
              'four and a half hours'
            )}
          </p>
        }
        actions={
          <>
            <button className="btn" onClick={() => navigate('/schedule')}>
              Timetable
            </button>
            {/*
              * A planned session can be deleted, and so can a closed one that
              * recorded nothing: a slot opened by accident — a past one, which
              * is born closed — is a card that could otherwise never be edited
              * nor removed, and it sat on the timetable for ever.
              */}
            {(session.state === 'PLANNED' ||
              (session.papers.length === 0 && session.candidates.length === 0)) && (
              <button className="btn btn--danger" onClick={() => void remove()}>
                Delete
              </button>
            )}
            {/* Set by mistake and already running: stopped, not marked. */}
            {session.state === 'RUNNING' &&
              (session.papers.length > 0 || session.candidates.length > 0) && (
                <button className="btn btn--danger" onClick={() => void remove()}>
                  Cancel session
                </button>
              )}
            {open && (
              <button className="btn" disabled={busy} onClick={() => void closeNow()}>
                Close now
              </button>
            )}
            {open && (
              <button
                className="btn btn--primary"
                disabled={busy || session.papers.length === 0 || session.candidates.length === 0}
                onClick={() => void setPapersOnRoom()}
              >
                {busy ? 'Working…' : 'Set the papers'}
              </button>
            )}
          </>
        }
      />

      <div className="stack">
        {error && <Banner tone="error">{error}</Banner>}
        {notice && <Banner tone="success">{notice}</Banner>}
        {warning && <Banner tone="warning">{warning}</Banner>}

        {session.state === 'CLOSED' && (
          <Banner tone="warning">
            {session.papers.length === 0 && session.candidates.length === 0
              ? 'This session closed with nothing in it — its four and a half hours were over before it was scheduled, which is what happens to a slot in the past. There is nothing here to record, so you can delete it.'
              : 'This session has closed. What is below is a record of what happened in it.'}
          </Banner>
        )}

        <section className="panel stack">
          <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <div>
              <h2 className="panel__title">The room</h2>
              <p className="muted">
                <span className="mono">{session.candidates.length}</span> sitting ·{' '}
                <span className="mono">{session.papers.length}</span>{' '}
                {session.papers.length === 1 ? 'paper' : 'papers'} ·{' '}
                <span className="mono">{session.tests}</span>{' '}
                {session.tests === 1 ? 'whole test' : 'whole tests'}
                {attendance && attendance.started > 0 && (
                  <>
                    {' '}
                    · <span className="mono">{attendance.started}</span> started ·{' '}
                    <span className="mono">{attendance.finished}</span> finished
                  </>
                )}
              </p>
            </div>

            <label className="row" style={{ gap: 'var(--s-2)', cursor: open ? 'pointer' : 'default' }}>
              <input
                type="checkbox"
                aria-label="Deal a different test to each candidate"
                checked={session.shuffle}
                disabled={!open || busy || (!session.canShuffle && !session.shuffle)}
                onChange={(e) =>
                  void save(
                    () => api.patch(`/sessions/${id}`, { shuffle: e.target.checked }),
                    e.target.checked
                      ? 'The papers will be dealt around the room.'
                      : 'Everybody will sit the same papers.',
                  )
                }
              />
              <span>Deal a different test to each candidate</span>
            </label>
          </div>

          <p className="field__hint" style={{ maxWidth: '68ch' }}>
            {session.canShuffle || session.shuffle
              ? 'Each candidate is dealt one whole test — its Listening, its Reading and its Writing — and nobody sitting next to them on the register gets the same one.'
              : `Dealing needs more than seven whole tests to draw from. This session has ${session.tests}. Add more, or everybody sits the same papers.`}
          </p>
        </section>

        {open && (
          <div className="pair">
            <section className="panel stack">
              <div>
                <h2 className="panel__title">Papers</h2>
                <p className="muted">Published papers only — a draft cannot be sat.</p>
              </div>
              <input
                type="search"
                className="field__input"
                placeholder="Find a paper or a test"
                aria-label="Find a paper for this session"
                value={paperQuery}
                onChange={(e) => setPaperQuery(e.target.value)}
              />
              <div className="picker">
                {wholeTests.length === 0 && loosePapers.length === 0 ? (
                  <p className="muted">Nothing in the library matches.</p>
                ) : (
                  <>
                    {wholeTests.map((test) => {
                      const ids = test.papers.map((p) => p.id);
                      const all = ids.every((id) => chosen.has(id));
                      const some = !all && ids.some((id) => chosen.has(id));
                      return (
                        <label key={`${test.sharedFrom ?? 'own'}-${test.name}`} className="picker__row">
                          <input
                            type="checkbox"
                            aria-label={test.name}
                            checked={all}
                            ref={(box) => {
                              // Half a test on the desks is a state worth
                              // showing rather than rounding to "off".
                              if (box) box.indeterminate = some;
                            }}
                            disabled={busy}
                            onChange={() => void toggleTest(ids, all)}
                          />
                          <span>
                            <span style={{ fontWeight: 600 }}>{test.name}</span>
                            <span className="muted">
                              {' '}
                              · {test.papers.map((p) => p.module.toLowerCase()).join(', ')}
                              {/* Whose test it is, where two of the same name
                                  could otherwise be told apart only by luck. */}
                              {test.sharedFrom ? ` · shared by ${test.sharedFrom}` : ''}
                              {some ? ' · partly chosen' : ''}
                            </span>
                          </span>
                        </label>
                      );
                    })}

                    {loosePapers.length > 0 && (
                      <p className="picker__note">
                        Papers belonging to no test, for a session of one module
                      </p>
                    )}
                    {loosePapers.map((paper) => (
                      <label key={paper.id} className="picker__row">
                        <input
                          type="checkbox"
                          aria-label={paper.title}
                          checked={chosen.has(paper.id)}
                          disabled={busy}
                          onChange={() => void togglePaper(paper.id)}
                        />
                        <span>
                          <span style={{ fontWeight: 600 }}>{paper.title}</span>
                          <span className="muted">
                            {' '}
                            · {paper.module.toLowerCase()}
                            {paper.shelf === 'PAST' ? ' · past paper' : ''}
                          </span>
                        </span>
                      </label>
                    ))}
                  </>
                )}
              </div>
            </section>

            <section className="panel stack">
              <div>
                <h2 className="panel__title">Candidates</h2>
                <p className="muted">Your students, and the ones the centre shares.</p>
              </div>

              {classes.length > 0 && (
                <div className="groupbar__chips">
                  <span className="eyebrow">Classes</span>
                  {classes.map((name) => (
                    <button
                      key={name}
                      type="button"
                      className="chip"
                      disabled={busy}
                      onClick={() => void seatClass(name)}
                    >
                      {name}
                    </button>
                  ))}
                </div>
              )}

              <input
                type="search"
                className="field__input"
                placeholder="Find a candidate by name, number or class"
                aria-label="Find a candidate for this session"
                value={studentQuery}
                onChange={(e) => setStudentQuery(e.target.value)}
              />
              <div className="picker">
                {roster.length === 0 ? (
                  <p className="muted">Nobody on your register matches.</p>
                ) : (
                  roster.map((student) => (
                    <label key={student.id} className="picker__row">
                      <input
                        type="checkbox"
                        aria-label={student.fullName}
                        checked={seated.has(student.id)}
                        disabled={busy}
                        onChange={() => void toggleStudent(student.id)}
                      />
                      <span>
                        <span style={{ fontWeight: 600 }}>{student.fullName}</span>
                        <span className="muted">
                          {' '}
                          · <span className="mono">{student.candidateNumber ?? '—'}</span>
                          {student.groupName ? ` · ${student.groupName}` : ''}
                        </span>
                      </span>
                    </label>
                  ))
                )}
              </div>
            </section>
          </div>
        )}

        {session.candidates.some((candidate) => candidate.sitting) && (
          <section className="panel stack">
            <div>
              <h2 className="panel__title">Who sits what</h2>
              <p className="muted" style={{ maxWidth: '62ch' }}>
                The deal. It holds: adding a latecomer does not move anybody already on a test.
              </p>
            </div>
            <div className="ledger__wrap">
              <table className="ledger">
                <thead>
                  <tr>
                    <th className="num">Candidate</th>
                    <th>Name</th>
                    <th>Test</th>
                  </tr>
                </thead>
                <tbody>
                  {session.candidates.map((candidate) => (
                    <tr key={candidate.id}>
                      <td className="num">{candidate.candidateNumber ?? '—'}</td>
                      <td>
                        <Person name={candidate.fullName} />
                      </td>
                      <td>{candidate.sitting ?? <span className="muted">not dealt yet</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {report && report.summary.sat > 0 && (
          <SessionResults report={report} downloadPath={`/sessions/${id}/results/export`} />
        )}
      </div>
    </>
  );
}
